import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usersService } from '../services/usersService';
import { organizationsService } from '../services/organizations.service';
import { User, UserRole, Organization } from '../types';
import { format } from 'date-fns';

const ROLE_LABELS: Record<string, string> = {
  platform_admin:    'Platform Admin',
  org_admin:         'Org Admin',
  manager:           'Müdür',
  staff:             'Personel',
  provider_manager:  'Tedarikçi Yöneticisi',
  provider_user:     'Tedarikçi Kullanıcısı',
  pool_member:       'Havuzcu',
};

const ROLE_BADGE: Record<string, string> = {
  platform_admin:   'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-300',
  org_admin:        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  manager:          'bg-blue-100   text-blue-800   dark:bg-blue-900/30   dark:text-blue-300',
  staff:            'bg-gray-100   text-gray-700   dark:bg-gray-700      dark:text-gray-300',
  provider_manager: 'bg-teal-100   text-teal-800   dark:bg-teal-900/30   dark:text-teal-300',
  provider_user:    'bg-cyan-100   text-cyan-800   dark:bg-cyan-900/30   dark:text-cyan-300',
  pool_member:      'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

const PlatformUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState<string>(searchParams.get('role') || 'all');
  const [orgFilter, setOrgFilter] = useState<string>(searchParams.get('org') || 'all');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, orgsData] = await Promise.all([
        usersService.getAll({
          organizationId: orgFilter !== 'all' ? orgFilter : undefined,
          isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
          includeDeleted: false,
        }),
        organizationsService.getAll(),
      ]);
      setUsers(usersData);
      setOrganizations(orgsData);
    } catch (err: any) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [orgFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync filters into URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (roleFilter !== 'all') params.role = roleFilter;
    if (orgFilter !== 'all') params.org = orgFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    setSearchParams(params, { replace: true });
  }, [search, roleFilter, orgFilter, statusFilter, setSearchParams]);

  const orgMap = Object.fromEntries(organizations.map(o => [o.id, o.name]));

  const resolveOrgName = (user: User): string => {
    if (user.organizationId && orgMap[user.organizationId]) return orgMap[user.organizationId];
    if (user.role === UserRole.PROVIDER_MANAGER || user.role === UserRole.PROVIDER_USER) return 'Tedarikçi hesabı';
    if (user.role === UserRole.POOL_MEMBER) return 'Havuz üyesi';
    return 'Belirsiz';
  };

  const filtered = users.filter(u => {
    const matchSearch =
      !search ||
      u.fullName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const roleCounts = users.reduce<Record<string, number>>((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Platform Kullanıcıları
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Tüm organizasyonlardaki kullanıcılar
          </p>
        </div>
        <button
          onClick={loadData}
          className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          ↻ Yenile
        </button>
      </div>

      {/* Role özet kartları */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {(['org_admin', 'manager', 'staff', 'provider_manager'] as const).map(role => (
          <button
            key={role}
            onClick={() => setRoleFilter(prev => prev === role ? 'all' : role)}
            className={`text-left p-3 rounded-xl border transition-all ${
              roleFilter === role
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-200'
            }`}
          >
            <p className="text-lg font-bold text-gray-900 dark:text-white">{roleCounts[role] || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ROLE_LABELS[role]}</p>
          </button>
        ))}
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="İsim veya e-posta ara…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={orgFilter}
            onChange={e => setOrgFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Organizasyonlar</option>
            {organizations.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Roller</option>
            {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'platform_admin').map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="inactive">İnaktif</option>
          </select>
        </div>
      </div>

      {/* Sonuç sayısı */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {loading ? 'Yükleniyor…' : `${filtered.length} kullanıcı gösteriliyor`}
        </p>
        {(search || roleFilter !== 'all' || orgFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setRoleFilter('all'); setOrgFilter('all'); setStatusFilter('all'); }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Filtreleri temizle
          </button>
        )}
      </div>

      {/* Tablo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 dark:text-gray-500 text-sm">
            Arama kriterine uyan kullanıcı bulunamadı.
          </div>
        ) : (
          <>
            {/* Desktop tablo */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kullanıcı</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organizasyon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rol</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kayıt Tarihi</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filtered.map(user => (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                      onClick={() => user.organizationId && navigate(`/app/organizations/${user.organizationId}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                              {user.fullName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.fullName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {resolveOrgName(user)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_BADGE[user.role] || ROLE_BADGE.staff}`}>
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          user.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {user.isActive ? 'Aktif' : 'İnaktif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {format(new Date(user.createdAt), 'dd.MM.yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {user.organizationId && (
                          <span className="text-blue-500 text-sm">Org →</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobil kartlar */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map(user => (
                <div
                  key={user.id}
                  className="px-4 py-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 active:bg-gray-100"
                  onClick={() => user.organizationId && navigate(`/app/organizations/${user.organizationId}`)}
                >
                  <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                      {user.fullName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{user.fullName}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_BADGE[user.role] || ROLE_BADGE.staff}`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {resolveOrgName(user)}
                    </p>
                  </div>
                  <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${user.isActive ? 'bg-green-400' : 'bg-red-400'}`} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformUsersPage;
